import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAppSelector } from '@/app/hooks';
import { OrderItem } from '@/types/supervisor';
import type { OrderItem as StoreOrderItem } from '@/features/order/orderSlice';
import {
  selectOrderItems,
} from '@/features/order/orderSlice';
import { getPrinters, type Printer } from '@/services/productService';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';

type OrderItemWithMetadata = Omit<OrderItem, 'uploadedFileId'> & { uploadedFileId?: string | null };

interface SetPrinterWindowProps {
  readonly item?: OrderItemWithMetadata | null;
  readonly onClose: () => void;
  readonly onBack?: () => void;
  readonly onSave: (printerId: string) => void;
  readonly saveLabel?: string;
  readonly selectedPrinterId?: string | null;
  readonly onSelectPrinter?: (printerId: string) => void;
  readonly onConfirmSelection?: (printerId: string) => void;
  readonly onApplyPrinterToAll?: (printerId: string) => Promise<void> | void;
  readonly draftPrinterId?: string | null;
  readonly onDraftChange?: (printerId: string | null) => void;
  readonly onDraftReset?: () => void;
}

const normalizeOrderItem = (source?: StoreOrderItem | null): OrderItemWithMetadata | null => {
  if (!source) {
    return null;
  }

  const orderItemId = source.id;
  if (!orderItemId) {
    return null;
  }

  const uploadedFileId = typeof source.uploadedFileId === 'string' && source.uploadedFileId.length > 0
    ? source.uploadedFileId
    : null;

  const resolveStatus = (): OrderItem['status'] => {
    const upper = typeof source.status === 'string' ? source.status.trim().toUpperCase() : undefined;
    if (upper === 'IN_ORDER' || upper === 'READY_TO_PRINT' || upper === 'PRINTED') {
      return upper;
    }
    return 'NEW';
  };

  const normalized: OrderItemWithMetadata = {
    id: orderItemId,
    uploadedFileId,
    orderId: source.orderId ?? null,
    paperTypeId: source.paperTypeId ?? undefined,
    printerId: source.printerId ?? undefined,
    width: source.width ?? undefined,
    height: source.height ?? undefined,
    widthIn: source.widthIn ?? undefined,
    heightIn: source.heightIn ?? undefined,
    originalWidthPx: source.originalWidthPx ?? undefined,
    originalHeightPx: source.originalHeightPx ?? undefined,
    status: resolveStatus(),
    createdAt: source.createdAt ? String(source.createdAt) : undefined,
    updatedAt: source.updatedAt ? String(source.updatedAt) : undefined,
    price: source.price ?? undefined,
    pricingStatus: (source.pricingStatus as OrderItem['pricingStatus']) ?? undefined,
    pricingError: source.pricingError ?? undefined,
  };

  return normalized;
};

const useSyncedOrderItem = (identifier: string | null | undefined): OrderItemWithMetadata | null => {
  const items = useAppSelector(selectOrderItems);

  return useMemo(() => {
    if (!identifier) {
      return null;
    }
    const match = items.find(candidate => candidate?.id === identifier);
    return normalizeOrderItem(match) ?? null;
  }, [identifier, items]);
};

const SetPrinterWindow: React.FC<SetPrinterWindowProps> = ({
  item: incomingItem,
  onClose,
  onBack,
  onSave,
  saveLabel,
  selectedPrinterId: controlledSelectedPrinterId,
  onSelectPrinter,
  onConfirmSelection,
  onApplyPrinterToAll,
  draftPrinterId,
  onDraftChange,
  onDraftReset,
}) => {
  const { t } = useTranslation();
  const currentIdentifier = incomingItem?.id ?? null;
  const trackedItem = useSyncedOrderItem(currentIdentifier);
  const item = trackedItem ?? incomingItem ?? null;

  const [printers, setPrinters] = useState<Printer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [internalSelectedPrinterId, setInternalSelectedPrinterId] = useState<string | null>(item?.printerId ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isApplyingAll, setIsApplyingAll] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPrinters = async () => {
      try {
        setIsLoading(true);
        const fetched = await getPrinters();
        if (cancelled) {
          return;
        }
        setPrinters(fetched);
        setLoadError(null);
      } catch (error) {
        logging.error('[SetPrinterWindow] Failed to load printers', { error });
        if (!cancelled) {
          setLoadError(t('setPrinter.loadError', 'Unable to load printers.'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchPrinters();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const draftChangeHandler: ((printerId: string | null) => void) | null =
    typeof onDraftChange === 'function' ? onDraftChange : null;
  const isControlled = typeof controlledSelectedPrinterId !== 'undefined';
  const isDraftControlled = draftChangeHandler !== null;
  const draftSelection = typeof draftPrinterId === 'undefined' ? null : draftPrinterId;
  const effectiveSelectedPrinterId = isDraftControlled
    ? (draftSelection ?? item?.printerId ?? null)
    : (isControlled ? controlledSelectedPrinterId ?? null : internalSelectedPrinterId);

  const emitDraftChange = useCallback((next: string | null) => {
    if (draftChangeHandler) {
      draftChangeHandler(next);
    }
  }, [draftChangeHandler]);

  useEffect(() => {
    if (isDraftControlled) {
      return;
    }
    if (!isControlled) {
      setInternalSelectedPrinterId(item?.printerId ?? null);
    }
  }, [isControlled, isDraftControlled, item?.id, item?.printerId]);

  const handleClose = useCallback(() => {
    analytics.track('button: ui.printer_close', { itemId: item?.id ?? null });
    onClose();
  }, [item?.id, onClose]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    handleClose();
  }, [handleClose, onBack]);

  const handleSelectPrinter = useCallback((printerId: string) => {
    analytics.track('button: ui.printer_select', { itemId: item?.id ?? null, printerId });
    if (isDraftControlled) {
      emitDraftChange(printerId);
      return;
    }
    if (isControlled) {
      onSelectPrinter?.(printerId);
      return;
    }
    setInternalSelectedPrinterId(printerId);
  }, [draftChangeHandler, isControlled, isDraftControlled, item?.id, onSelectPrinter]);

  const selectedPrinter = useMemo(() => {
    if (!effectiveSelectedPrinterId) {
      return null;
    }
    return printers.find(candidate => candidate.id === effectiveSelectedPrinterId) ?? null;
  }, [effectiveSelectedPrinterId, printers]);

  const handlePersist = useCallback(async () => {
    if (!item?.id || !effectiveSelectedPrinterId) {
      return;
    }

    try {
      setIsSaving(true);
      await Promise.resolve(onSave(effectiveSelectedPrinterId));
      analytics.track('button: ui.printer_save', { itemId: item.id, printerId: effectiveSelectedPrinterId });

      if (isDraftControlled) {
        emitDraftChange(null);
        return;
      }

      onDraftReset?.();
      emitDraftChange(null);
      handleClose();
    } finally {
      setIsSaving(false);
    }
  }, [emitDraftChange, effectiveSelectedPrinterId, handleClose, isDraftControlled, item?.id, onSave, onDraftReset]);

  const handleApplyToAll = useCallback(async () => {
    if (!effectiveSelectedPrinterId || !onApplyPrinterToAll) {
      return;
    }
    try {
      setIsApplyingAll(true);
      analytics.track('button: ui.printer_apply_all', { printerId: effectiveSelectedPrinterId });
      await Promise.resolve(onApplyPrinterToAll(effectiveSelectedPrinterId));
    } finally {
      setIsApplyingAll(false);
    }
  }, [effectiveSelectedPrinterId, onApplyPrinterToAll]);

  if (!item) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-muted-foreground">
          {t('setPrinter.noItem', 'Select an item to choose its printer.')}
        </p>
        <Button variant="outline" onClick={handleBack}>
          {t('common.back', 'Back')}
        </Button>
      </div>
    );
  }

  const headerTitle = t('setPrinter.title', 'Choose printer');
  const headerSubtitle = t('setPrinter.subtitle', 'Match this item with the right printer.');
  const saveButtonLabel = saveLabel ?? t('setPrinter.confirmCta', 'Confirm printer');

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden bg-background p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label={t('common.back', 'Back')}>
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </Button>
          <div>
            <p className="text-lg font-semibold text-foreground sm:text-xl">{headerTitle}</p>
            <p className="text-sm text-muted-foreground">{headerSubtitle}</p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {onApplyPrinterToAll ? (
            <Button
              variant="secondary"
              onClick={handleApplyToAll}
              disabled={isSaving || isApplyingAll || !effectiveSelectedPrinterId}
            >
              {isApplyingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('setPrinter.applyAllCta', 'Apply to all items')}
            </Button>
          ) : null}
          <Button onClick={handlePersist} disabled={isSaving || !effectiveSelectedPrinterId}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {saveButtonLabel}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 py-4">
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-6">
          <section className="flex flex-col gap-4 rounded-2xl border border-border bg-muted/10 p-4 sm:p-6">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-muted-foreground">
                  {t('setPrinter.selectionLabel', 'Available printers')}
                </span>
                <span className="text-xs text-muted-foreground sm:text-sm">
                  {t('setPrinter.selectionHelper', 'Select a printer to see its details.')}
                </span>
              </div>
            </header>

            <ScrollArea className="max-h-[60vh] pr-2">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {isLoading ? (
                  <div className="col-span-full flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>{t('common.loading', 'Loading...')}</span>
                  </div>
                ) : null}

                {loadError ? (
                  <div className="col-span-full rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                    {loadError}
                  </div>
                ) : null}

                {!isLoading && !loadError && printers.length === 0 ? (
                  <div className="col-span-full rounded-lg border border-border bg-background/70 p-6 text-center text-sm text-muted-foreground">
                    {t('setPrinter.emptyState', 'No printers available right now.')}
                  </div>
                ) : null}

                {printers.map(printer => {
                  const isSelected = effectiveSelectedPrinterId === printer.id;
                  const priceIndexValue = typeof printer.priceIndex === 'number' && Number.isFinite(printer.priceIndex)
                    ? printer.priceIndex
                    : null;
                  const priceIndexLabel = priceIndexValue != null ? priceIndexValue.toFixed(2) : '—';
                  const subtitleParts = [printer.brand?.trim(), printer.model?.trim()].filter(Boolean);
                  return (
                    <button
                      key={printer.id}
                      type="button"
                      onClick={() => handleSelectPrinter(printer.id)}
                      onDoubleClick={() => {
                        analytics.track('button: ui.printer_confirm_double', { itemId: item?.id ?? null, printerId: printer.id });
                        handleSelectPrinter(printer.id);
                        onConfirmSelection?.(printer.id);
                      }}
                      className={`flex flex-col rounded-xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring focus-visible:ring-primary/50 ${
                        isSelected
                          ? 'border-primary bg-primary/10 shadow'
                          : 'border-border bg-card hover:border-primary/40'
                      }`}
                    >
                      <span className="text-sm font-semibold text-foreground">
                        {printer.name || t('setPrinter.unnamedPrinter', 'Printer')}
                      </span>
                      {subtitleParts.length > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          {subtitleParts.join(' • ')}
                        </span>
                      ) : null}
                      <span className="mt-1 text-xs text-muted-foreground">
                        {t('setPrinter.priceIndexInline', 'Price index: {{index}}', { index: priceIndexLabel })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </section>

          <aside className="flex min-h-[320px] flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            {selectedPrinter ? (
              selectedPrinter.description ? (
                <ScrollArea className="h-full flex-1 p-4 sm:p-6">
                  <div
                    className="prose prose-sm dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: selectedPrinter.description ?? '' }}
                  />
                </ScrollArea>
              ) : (
                <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
                  {t('setPrinter.noDescription', 'This printer does not have a description yet.')}
                </div>
              )
            ) : (
              <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                <p>{t('setPrinter.noPrinterSelected', 'Select a printer to see its specifications.')}</p>
              </div>
            )}
          </aside>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:hidden">
        <Button variant="outline" onClick={handleClose}>
          {t('common.cancel', 'Cancel')}
        </Button>
        {onApplyPrinterToAll ? (
          <Button
            variant="secondary"
            onClick={handleApplyToAll}
            disabled={isSaving || isApplyingAll || !effectiveSelectedPrinterId}
          >
            {isApplyingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('setPrinter.applyAllCta', 'Apply to all items')}
          </Button>
        ) : null}
        <Button onClick={handlePersist} disabled={isSaving || !effectiveSelectedPrinterId}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {saveButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export default SetPrinterWindow;
